/**
 * Exportación e importación del archivo `.gemba.json` (doc. 03 §6): formato y versión de esquema,
 * validación estricta, migración automática y rechazo claro de archivos de versiones más nuevas.
 */

import type { Proyecto } from '../nucleo/dominio/espacio';
import { VERSION_ESQUEMA } from '../nucleo/dominio/espacio';
import { ulid } from 'ulid';
import { esquemaArchivo, esquemaProyecto, FORMATO_ARCHIVO } from './esquema';
import { esArchivoV6, migrarArchivoV6 } from './migradorV6';
import { ErrorDeMigracion, migrarDocumento } from './migraciones';

/**
 * El archivo declara si el proyecto viene de un espacio de trabajo con datos reales (doc. 02 §8):
 * al importarlo, solo entra a otro espacio de trabajo con esa marca.
 */
export function exportarProyecto(
  proyecto: Proyecto,
  versionApp: string,
  exportadoEn: string,
  contieneDatosReales = false,
): string {
  return exportarDocumento(proyecto, versionApp, exportadoEn, contieneDatosReales);
}

/**
 * Exporta un documento guardado tal como está, con su propio esquema (por ejemplo, una versión del
 * historial guardada con un esquema anterior): al importarlo se convierte como cualquier archivo.
 */
export function exportarDocumento(
  documento: unknown,
  versionApp: string,
  exportadoEn: string,
  contieneDatosReales = false,
): string {
  const d = documento as { versionEsquema?: unknown };
  const archivo = {
    formato: FORMATO_ARCHIVO,
    versionEsquema: typeof d.versionEsquema === 'number' ? d.versionEsquema : VERSION_ESQUEMA,
    exportadoEn,
    versionApp,
    contieneDatosReales,
    proyecto: documento,
    ejecuciones: [],
  };
  return JSON.stringify(archivo, null, 2);
}

export function nombreDeArchivo(proyecto: Pick<Proyecto, 'nombre'>): string {
  const base = proyecto.nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `${base || 'proyecto'}.gemba.json`;
}

export type ResultadoImportacion =
  | {
      ok: true;
      proyecto: Proyecto;
      migradoDesde: number | null;
      contieneDatosReales?: boolean;
      /** Archivo del prototipo v6 (formato 4), con lo que no tuvo equivalente. */
      desdeV6?: { avisos: string[] };
    }
  | { ok: false; mensaje: string; detalles: string[] };

const fallo = (mensaje: string, detalles: string[] = []): ResultadoImportacion => ({
  ok: false,
  mensaje,
  detalles,
});

export function importarArchivo(
  texto: string,
  {
    nuevoId = ulid,
    ahora = new Date().toISOString(),
  }: { nuevoId?: () => string; ahora?: string } = {},
): ResultadoImportacion {
  let datos: unknown;
  try {
    datos = JSON.parse(texto);
  } catch {
    return fallo(
      'El archivo no es un JSON válido. ¿Es un archivo .gemba.json exportado por Gemba Studio?',
    );
  }
  if (esArchivoV6(datos)) return importarDelV6(datos, nuevoId, ahora);
  const archivo = esquemaArchivo.safeParse(datos);
  if (!archivo.success) {
    return fallo(
      'El archivo no tiene el formato de un proyecto de Gemba Studio.',
      archivo.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  }
  if (archivo.data.versionEsquema > VERSION_ESQUEMA) {
    return fallo(
      `El archivo es de una versión más nueva de Gemba Studio (esquema ${archivo.data.versionEsquema}; esta aplicación usa el ${VERSION_ESQUEMA}). Actualiza la aplicación para abrirlo.`,
    );
  }
  if (typeof archivo.data.proyecto !== 'object' || archivo.data.proyecto === null) {
    return fallo('El archivo no contiene un proyecto.');
  }
  let migrado;
  try {
    migrado = migrarDocumento(archivo.data.proyecto as Record<string, unknown>);
  } catch (e) {
    return fallo(e instanceof ErrorDeMigracion ? e.message : 'No se pudo leer el proyecto.');
  }
  const proyecto = esquemaProyecto.safeParse(migrado.documento);
  if (!proyecto.success) {
    return fallo(
      'El proyecto del archivo tiene datos inválidos y no se importó. Tus proyectos actuales no cambiaron.',
      proyecto.error.issues.slice(0, 10).map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  }
  return {
    ok: true,
    proyecto: proyecto.data as Proyecto,
    migradoDesde: migrado.desde < migrado.hasta ? migrado.desde : null,
    contieneDatosReales: archivo.data.contieneDatosReales,
  };
}

/** Archivo del v6: se convierte y se valida igual que cualquier proyecto. */
function importarDelV6(datos: unknown, nuevoId: () => string, ahora: string): ResultadoImportacion {
  const r = migrarArchivoV6(datos, nuevoId, ahora);
  if (!r.ok) return fallo(r.mensaje, r.detalles);
  const proyecto = esquemaProyecto.safeParse(r.proyecto);
  if (!proyecto.success)
    return fallo(
      'El archivo del v6 tiene datos que no se pudieron convertir y no se importó. Tus proyectos actuales no cambiaron.',
      proyecto.error.issues.slice(0, 10).map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  return {
    ok: true,
    proyecto: proyecto.data as Proyecto,
    migradoDesde: null,
    contieneDatosReales: false,
    desdeV6: { avisos: r.avisos },
  };
}

/** Valida un documento guardado (al abrirlo desde el almacenamiento local). */
export function validarDocumento(documento: unknown): ResultadoImportacion {
  if (typeof documento !== 'object' || documento === null) return fallo('Documento vacío.');
  try {
    const migrado = migrarDocumento(documento as Record<string, unknown>);
    const r = esquemaProyecto.safeParse(migrado.documento);
    if (!r.success)
      return fallo(
        'El proyecto guardado tiene datos inválidos.',
        r.error.issues.slice(0, 10).map((i) => `${i.path.join('.')}: ${i.message}`),
      );
    return {
      ok: true,
      proyecto: r.data as Proyecto,
      migradoDesde: migrado.desde < migrado.hasta ? migrado.desde : null,
    };
  } catch (e) {
    return fallo(e instanceof Error ? e.message : 'No se pudo leer el proyecto.');
  }
}
